'use client'

/**
 * The Email Remediator in the activity dock: the words for its three kinds of work and the buttons
 * that call ITS endpoints.
 *
 *   er.snr.search           a Search & Remediate run     pause / resume / cancel / retry failed mailboxes
 *   er.snr.action           a remediation job            pause / resume / cancel, retry failed copies, undo
 *   er.snr.org_remediation  one reported mail, every copy   cancel
 *
 * Undo puts each copy back where it was. Every button is a PermissionButton on update:ErSearchRemediate
 * (the check of the endpoint it calls); the API still refuses what the role cannot do.
 *
 * THE DOCK AND THE S&R PAGE DO THE SAME THING THE SAME WAY: a job's Pause / Resume / Cancel go through the page's
 * hook (useSnrJobControl), and Undo through the page's flow (useSnrUndoFlow): the undo preview first, the plain
 * words about mail returning to inboxes, and above 25 copies the typed count. The two cannot disagree.
 *
 * A JOB THAT PAUSED ITSELF for a missing permission (owner, 27 Oct 2026) says why on its card: what is missing
 * (the provider refused the app, the hard-delete switch is off, Google has not granted permanent delete) and the
 * mailbox, from the adapter's `detail.pause*` values, in the viewer's language; Resume carries on once it is
 * granted. A paused permanent Delete resumes only for someone the Delete gate lets through (Resume is OFF with the
 * reason otherwise), and a Delete is never offered an Undo (the server sends none).
 */

import * as React from 'react'
import Link from 'next/link'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Ban, ExternalLink, Loader2, Pause, Play, RotateCcw, Undo2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DisabledWithReason } from '@/components/ui/disabled-with-reason'
import { UserText } from '@/components/ui/user-text'
import { PermissionButton } from '@/components/auth/permission-gate'
import { snrActionLabel, snrPauseText, snrReasonLabel } from '@/components/er/search-remediate/snr-labels'
import { SNR_READ, SNR_REMEDIATE } from '@/components/er/search-remediate/snr-permissions'
import { useSnrUndoFlow } from '@/components/er/search-remediate/snr-undo-flow'
import { useSnrVerbGate } from '@/components/er/search-remediate/snr-verb-gate'
import type { SnrMissingPermission, SnrProvider } from '@/lib/er/snr-query/types'
import { erFetch } from '@/lib/er/api'
import { erKeys } from '@/lib/er/query-keys'
import { useSnrJobControl, useSnrRetryFailed, useSnrRetryRun, useSnrRunControl } from '@/lib/er/hooks/use-search-remediate-v2'
import { refuseSnr } from '@/lib/er/snr-refusal'
import { notifyActivitiesChanged, openActivityDock } from '@/lib/activities/events'
import { handled, type ActivityActionKey, type ActivityDto } from '@/lib/activities/types'
import { useTranslation } from '@/lib/i18n'
import type { ActivityRenderer } from './activity-dock-renderers'

const SEARCH = 'er.snr.search'
const ACTION = 'er.snr.action'
const ORG = 'er.snr.org_remediation'

const text = (v: string | number | null | undefined): string => (v === null || v === undefined ? '' : String(v))

/** The verb of a job, in words; a Move names its folder. */
function verbOf(a: ActivityDto, t: Parameters<ActivityRenderer['titleText']>[1]): string {
  return snrActionLabel(t, text(a.detail.action))
}

function jobTitleKey(a: ActivityDto): string {
  if (a.detail.jobKind === 'retry') return 'activities.er.action.titleRetry'
  if (a.detail.jobKind === 'undo') return 'activities.er.action.titleUndo'
  return 'activities.er.action.title'
}

const post = (): RequestInit => ({ method: 'POST' })

const MISSING: ReadonlySet<string> = new Set(['role', 'switch', 'provider_scope', 'mail_permission'])

/** The pause a job put itself in (a missing permission), from the adapter's detail; null otherwise. */
export function erActivityPause(a: ActivityDto): { missing: SnrMissingPermission; provider: SnrProvider | null; mailbox: string | null } | null {
  if (a.kind !== ACTION || a.state !== 'paused' || a.detail.pauseReason !== 'permission_missing') return null
  const missing = text(a.detail.pauseMissing)
  const provider = text(a.detail.pauseProvider)
  return {
    missing: (MISSING.has(missing) ? missing : 'mail_permission') as SnrMissingPermission,
    provider: provider === 'microsoft' || provider === 'google' ? provider : null,
    mailbox: text(a.detail.pauseMailbox) || null,
  }
}

/** Cancel an org-wide remediation: the existing "cancel the remediation phase" route of its log. */
function useErOrgCancel() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (logId: string) => erFetch<{ success: boolean }>(`search-remediate/${logId}/cancel-remediation`, post()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: erKeys.searchRemediate.all() })
      notifyActivitiesChanged()
    },
  })
}

function ErActivityActions({ activity }: { activity: ActivityDto }) {
  const { t } = useTranslation()
  const [confirming, setConfirming] = React.useState(false)
  const runId = activity.kind === ACTION ? text(activity.detail.runId) : activity.refId
  const runControl = useSnrRunControl(activity.kind === SEARCH ? activity.refId : null)
  const retryRun = useSnrRetryRun(activity.kind === SEARCH ? activity.refId : null)
  const retryJob = useSnrRetryFailed(runId)
  // A retry or an undo is a new job: open the dock on it.
  const followChild = (r: { jobId: string }) => {
    notifyActivitiesChanged()
    openActivityDock(`${ACTION}:${r.jobId}`)
  }
  const jobControl = useSnrJobControl(runId)
  // Undo is the page's own flow: it previews, says what an undo does in plain words, and asks for the count above 25.
  const undoFlow = useSnrUndoFlow(runId, (undoJobId) => followChild({ jobId: undoJobId }))
  const orgCancel = useErOrgCancel()
  const verbGate = useSnrVerbGate()
  const pause = erActivityPause(activity)
  // A paused permanent Delete runs again only for someone the Delete gate lets through.
  const resumeGate = activity.kind === ACTION && text(activity.detail.action) === 'Delete' ? verbGate('Delete') : { disabled: false, reason: null }

  const pending = runControl.isPending || retryRun.isPending || retryJob.isPending || undoFlow.isPending || jobControl.isPending || orgCancel.isPending
  const failed = (what: string) => ({ onError: refuseSnr(t, `Activity dock: ${what}`, 'activities.action.failed') })
  const done = { onSuccess: () => notifyActivitiesChanged() }

  const control = (key: 'pause' | 'resume' | 'cancel') => {
    setConfirming(false)
    if (activity.kind === SEARCH) runControl.mutate(key, { ...done, ...failed(`run ${key}`) })
    else if (activity.kind === ACTION) jobControl.mutate({ jobId: activity.refId, control: key }, failed(`job ${key}`))
    else if (activity.kind === ORG && key === 'cancel') orgCancel.mutate(activity.refId, failed('org-wide cancel'))
  }

  const run = (key: ActivityActionKey) => {
    switch (key) {
      case 'pause':
      case 'resume':
        return control(key)
      case 'cancel':
        return setConfirming(true)
      case 'retryFailed':
        return activity.kind === SEARCH
          ? retryRun.mutate(undefined, { ...done, ...failed('retry mailboxes') })
          : retryJob.mutate(activity.refId, { onSuccess: followChild, ...failed('retry copies') })
      case 'undo':
        return undoFlow.requestUndo(activity.refId)
    }
  }

  const button = (key: ActivityActionKey) => {
    const icon: Record<ActivityActionKey, React.ReactNode> = {
      pause: <Pause className="size-4" aria-hidden />,
      resume: <Play className="size-4" aria-hidden />,
      cancel: <Ban className="size-4" aria-hidden />,
      retryFailed: <RotateCcw className="size-4" aria-hidden />,
      undo: <Undo2 className="size-4" aria-hidden />,
    }
    const gated = (key === 'resume' || key === 'retryFailed') && resumeGate.disabled
    const element = (
      <PermissionButton
        key={key}
        action={SNR_REMEDIATE.action}
        subject={SNR_REMEDIATE.subject}
        size="sm"
        variant="outline"
        data-activity-action={key}
        disabled={pending || gated}
        onClick={() => run(key)}
      >
        {pending && key !== 'cancel' ? <Loader2 className="size-4 animate-spin motion-reduce:animate-none" aria-hidden /> : icon[key]}
        {t(`activities.action.${key}`)}
      </PermissionButton>
    )
    return gated ? (
      <DisabledWithReason key={key} disabled reason={resumeGate.reason}>
        {element}
      </DisabledWithReason>
    ) : (
      element
    )
  }

  const keys = activity.actions.filter((k) => !(k === 'cancel' && confirming))
  return (
    <>
      {pause && (
        <p className="basis-full text-xs text-amber-700 dark:text-amber-400" data-activity-pause-reason={pause.missing}>
          {snrPauseText(t, pause)}
          {pause.mailbox && (
            <>
              {' '}
              <bdi dir="ltr" className="font-medium">
                {pause.mailbox}
              </bdi>
            </>
          )}
        </p>
      )}
      {keys.map(button)}
      {confirming && (
        <span className="flex flex-wrap items-center gap-2" data-activity-confirm="cancel">
          <span className="text-xs">{t('activities.action.cancelAsk')}</span>
          <PermissionButton
            action={SNR_REMEDIATE.action}
            subject={SNR_REMEDIATE.subject}
            size="sm"
            variant="destructive"
            disabled={pending}
            onClick={() => control('cancel')}
          >
            {t('activities.action.cancelYes')}
          </PermissionButton>
          <Button size="sm" variant="outline" onClick={() => setConfirming(false)}>
            {t('activities.action.cancelNo')}
          </Button>
        </span>
      )}
      {undoFlow.dialogs}
      {activity.href && (
        <Button asChild size="sm" variant="ghost">
          <Link href={activity.href}>
            <ExternalLink className="size-4 rtl-flip" aria-hidden />
            {t('activities.er.open')}
          </Link>
        </Button>
      )}
    </>
  )
}

export const ER_ACTIVITY_RENDERER: ActivityRenderer = {
  requires: [SNR_READ],
  refreshKeys: [erKeys.searchRemediate.all()],
  moduleLabel: (t) => t('activities.er.moduleName'),

  titleText: (a, t) => {
    if (a.kind === SEARCH) return a.label ?? t('activities.er.search.title')
    if (a.kind === ORG) return t('activities.er.org.title', { action: verbOf(a, t), count: a.counts.total })
    const folder = text(a.detail.folderName)
    const title = t(jobTitleKey(a), { action: verbOf(a, t), count: a.counts.total })
    return folder ? `${title} (${folder})` : title
  },

  title: (a, t) => {
    if (a.kind === SEARCH) return a.label ? <UserText>{a.label}</UserText> : t('activities.er.search.title')
    if (a.kind === ORG) return t('activities.er.org.title', { action: verbOf(a, t), count: a.counts.total })
    const folder = text(a.detail.folderName)
    const title = t(jobTitleKey(a), { action: verbOf(a, t), count: a.counts.total })
    return folder ? (
      <>
        {title} <span aria-hidden>·</span> <UserText>{folder}</UserText>
      </>
    ) : (
      title
    )
  },

  pillLine: (a, t, n) => {
    const params = { done: n(handled(a.counts)), total: n(a.counts.total), matches: a.metrics.matches ?? 0 }
    if (a.kind === SEARCH) return t(a.state === 'finished' || a.state === 'finished_with_failures' ? 'activities.er.search.pillDone' : 'activities.er.search.pill', params)
    // Paused for a missing permission: the pill says so, so the analyst opens the card to see what is missing.
    if (erActivityPause(a)) return t('er.snr.pause.pill', { action: verbOf(a, t) })
    return t('activities.er.job.pill', { ...params, action: verbOf(a, t) })
  },

  tiles: (a, t, n) => {
    const c = a.counts
    if (a.kind === SEARCH) {
      const tiles = [
        { key: 'matches', label: t('er.snr.progress.matches'), value: n(a.metrics.matches ?? 0) },
        { key: 'searched', label: t('er.snr.progress.searched'), value: n(c.done) },
        { key: 'remaining', label: t('er.snr.progress.remaining'), value: n(c.remaining) },
        { key: 'failed', label: t('er.snr.progress.failedMailboxes'), value: n(c.failed), tone: c.failed > 0 ? ('bad' as const) : undefined },
      ]
      return c.skipped > 0 ? [...tiles, { key: 'skipped', label: t('er.snr.progress.skipped'), value: n(c.skipped) }] : tiles
    }
    return [
      { key: 'moved', label: t(text(a.detail.action) === 'Delete' ? 'er.snr.job.deleted' : 'er.snr.job.moved'), value: n(c.done) },
      { key: 'remaining', label: t('er.snr.job.remaining'), value: n(c.remaining) },
      { key: 'failed', label: t('er.snr.job.failed'), value: n(c.failed), tone: c.failed > 0 ? ('bad' as const) : undefined },
      { key: 'skipped', label: t('er.snr.job.skipped'), value: n(c.skipped) },
    ]
  },

  reasonLabel: (t, code) => snrReasonLabel(t, code),
  Actions: ErActivityActions,
}
